// /admin/ads → "מודעות": every ad as a video card (not a table) — play it, see what it
// brought (spend → leads → signups → paying), pause/resume/budget, start a new campaign from
// the same video, or tick it for an A/B test.
import {FC, useState} from 'react'
import {getVideoSource, Launch} from './_requests'
import LaunchForm from './LaunchForm'

const ils = (n: number | null | undefined) =>
  n == null || !isFinite(n) ? '—' : `₪${Math.round(n).toLocaleString('he-IL')}`
const errMsg = (e: any) => e?.response?.data?.error || e?.message || 'שגיאה'

const DEST: Record<string, string> = {
  website: '🌐 דף הרשמה',
  lead_form: '📝 טופס פייסבוק',
  whatsapp: '💬 וואטסאפ',
  other: 'אחר',
}

const STATUS: Record<string, {label: string; cls: string}> = {
  ACTIVE: {label: 'פעילה', cls: 'success'},
  PAUSED: {label: 'מושהית', cls: 'secondary'},
  CAMPAIGN_PAUSED: {label: 'מושהית', cls: 'secondary'},
  ADSET_PAUSED: {label: 'מושהית', cls: 'secondary'},
  WITH_ISSUES: {label: 'בעיה', cls: 'danger'},
  DISAPPROVED: {label: 'נדחתה', cls: 'danger'},
  IN_PROCESS: {label: 'בבדיקת פייסבוק', cls: 'info'},
  PENDING_REVIEW: {label: 'בבדיקת פייסבוק', cls: 'info'},
}

const AdsGrid: FC<{
  launches: Launch[]
  selected: string[]
  setSelected: (k: string[]) => void
  rowBusy: string | null
  onToggle: (l: Launch) => void
  onBudget: (l: Launch) => void
  onRemove: (l: Launch) => void
  defaultLink: string
  lookalikes: string[]
  onLaunched: () => void
  goTests: () => void
}> = ({
  launches,
  selected,
  setSelected,
  rowBusy,
  onToggle,
  onBudget,
  onRemove,
  defaultLink,
  lookalikes,
  onLaunched,
  goTests,
}) => {
  const [playing, setPlaying] = useState<Record<string, string>>({})
  const [open, setOpen] = useState<string | null>(null)
  const [error, setError] = useState('')

  const play = async (l: Launch) => {
    setError('')
    if (l.mp4) return setPlaying({...playing, [l._id]: l.mp4})
    if (!l.video_id) return setError('לא נמצא סרטון למודעה הזו (מודעת תמונה או פוסט שלא זוהה)')
    try {
      const {data} = await getVideoSource(l.video_id)
      if (data.source) setPlaying({...playing, [l._id]: data.source})
      else setError('פייסבוק לא החזיר קישור לניגון')
    } catch (e) {
      setError(errMsg(e))
    }
  }

  const toggleSel = (k: string) =>
    setSelected(selected.includes(k) ? selected.filter((x) => x !== k) : [...selected, k])

  return (
    <div style={{paddingBottom: selected.length ? 80 : 0}}>
      {error && <div className='alert alert-danger py-2 fs-7'>{error}</div>}
      <div
        className='d-grid gap-4 mb-5'
        style={{gridTemplateColumns: 'repeat(auto-fill, minmax(min(250px, 100%), 1fr))'}}
      >
        {launches.map((l) => {
          const spend = l.meta?.spend || 0
          const f = l.funnel
          const leads = (f.form_leads || 0) + (f.wa_conversations || 0)
          const eff =
            l.status === 'creating'
              ? 'creating'
              : l.status === 'failed'
              ? 'failed'
              : l.meta?.effective_status || l.status
          const st =
            eff === 'creating'
              ? {label: 'נבנית…', cls: 'info'}
              : eff === 'failed'
              ? {label: 'נכשלה', cls: 'danger'}
              : STATUS[eff] || {label: eff, cls: 'secondary'}
          const key =
            l.creative_key || (l.slug ? 'reel:' + l.slug : l.video_id ? 'video:' + l.video_id : '')
          const sel = !!key && selected.includes(key)
          return (
            <div
              key={l._id}
              className='card card-bordered p-3'
              style={sel ? {outline: '3px solid #2b70fa'} : undefined}
            >
              <div style={{position: 'relative'}}>
                {playing[l._id] ? (
                  <video
                    src={playing[l._id]}
                    controls
                    autoPlay
                    playsInline
                    style={{
                      width: '100%',
                      aspectRatio: '9 / 16',
                      borderRadius: 10,
                      background: '#000',
                    }}
                  />
                ) : (
                  <div
                    onClick={() => play(l)}
                    style={{
                      width: '100%',
                      aspectRatio: '9 / 16',
                      borderRadius: 10,
                      background: l.thumb
                        ? `#111 url("${l.thumb}") center/cover no-repeat`
                        : '#1e293b',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <span
                      style={{fontSize: 44, color: '#fff', textShadow: '0 2px 8px rgba(0,0,0,.6)'}}
                    >
                      ▶
                    </span>
                  </div>
                )}
                {key && (
                  <label
                    className='form-check'
                    style={{
                      position: 'absolute',
                      top: 8,
                      insetInlineStart: 8,
                      background: '#fff',
                      borderRadius: 8,
                      padding: '4px 8px 4px 30px',
                      margin: 0,
                    }}
                  >
                    <input
                      className='form-check-input'
                      type='checkbox'
                      checked={sel}
                      onChange={() => toggleSel(key)}
                    />
                    <span className='form-check-label fs-8 fw-bold'>לבדיקה</span>
                  </label>
                )}
                <span
                  className={`badge badge-${st.cls}`}
                  style={{position: 'absolute', top: 8, insetInlineEnd: 8}}
                >
                  {st.label}
                </span>
              </div>

              <div className='mt-2'>
                <div className='fw-bold fs-7 text-truncate' title={l.name}>
                  {l.source === 'imported' && (
                    <span className='badge badge-light-dark me-1'>קיימת</span>
                  )}
                  {l.name}
                </div>
                <div className='text-muted fs-8'>
                  {DEST[l.destination || 'website']} ·{' '}
                  {l.daily_budget ? `${ils(l.daily_budget)}/יום` : 'תקציב כולל'}
                </div>
                {(l.error || (l.meta?.issues || []).length > 0) && (
                  <div
                    className='text-danger fs-8 text-truncate'
                    title={l.error || (l.meta?.issues || []).join(' · ')}
                  >
                    {l.error || (l.meta?.issues || [])[0]}
                  </div>
                )}
              </div>

              <div className='d-grid gap-1 mt-2 fs-8' style={{gridTemplateColumns: '1fr 1fr 1fr'}}>
                <div>
                  <div className='text-muted'>הוצאה</div>
                  <b>{ils(spend)}</b>
                </div>
                <div>
                  <div className='text-muted'>לידים</div>
                  <b>{leads || l.meta?.fb_registrations || '—'}</b>
                </div>
                <div>
                  <div className='text-muted'>עלות לליד</div>
                  <b>{leads ? ils(spend / leads) : '—'}</b>
                </div>
                <div>
                  <div className='text-muted'>הרשמות</div>
                  <b>{f.signups}</b>
                </div>
                <div>
                  <div className='text-muted'>קמפיין→פוסט</div>
                  <b>
                    {f.campaign || 0}→{f.post || 0}
                  </b>
                </div>
                <div>
                  <div className='text-muted'>חיברו פייסבוק</div>
                  <b>{f.connected}</b>
                </div>
                <div>
                  <div className='text-muted'>שילמו</div>
                  <b>{f.paid}</b>
                </div>
                <div>
                  <div className='text-muted'>הכנסה</div>
                  <b>{ils(f.revenue)}</b>
                </div>
              </div>

              <div className='d-flex gap-1 flex-wrap mt-3'>
                {(l.status === 'ACTIVE' || l.status === 'PAUSED') && (
                  <>
                    <button
                      className={`btn btn-sm ${
                        l.status === 'ACTIVE' ? 'btn-light-warning' : 'btn-light-success'
                      }`}
                      disabled={rowBusy === l._id}
                      onClick={() => onToggle(l)}
                    >
                      {l.status === 'ACTIVE' ? 'השהה' : 'הפעל'}
                    </button>
                    {!l.has_lifetime_budget && (
                      <button
                        className='btn btn-sm btn-light'
                        disabled={rowBusy === l._id}
                        onClick={() => onBudget(l)}
                      >
                        תקציב
                      </button>
                    )}
                  </>
                )}
                {l.status === 'failed' && (
                  <button
                    className='btn btn-sm btn-light-danger'
                    disabled={rowBusy === l._id}
                    onClick={() => onRemove(l)}
                  >
                    הסר
                  </button>
                )}
                {key && (
                  <button
                    className={`btn btn-sm ${
                      open === l._id ? 'btn-light' : 'btn-light-primary'
                    } flex-fill`}
                    onClick={() => setOpen(open === l._id ? null : l._id)}
                  >
                    {open === l._id ? 'סגור' : '🚀 קמפיין חדש מהסרטון'}
                  </button>
                )}
              </div>
              {open === l._id && key && (
                <LaunchForm
                  creative={{key, headline: l.headline || '', text: (l as any).primary || ''}}
                  defaultLink={defaultLink}
                  lookalikes={lookalikes}
                  onDone={() => {
                    setOpen(null)
                    onLaunched()
                  }}
                />
              )}
            </div>
          )
        })}
      </div>

      {selected.length > 0 && (
        <div
          className='d-flex align-items-center gap-3 flex-wrap p-4 shadow'
          style={{
            position: 'fixed',
            bottom: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            background: '#1e293b',
            color: '#fff',
            borderRadius: 14,
            zIndex: 50,
          }}
        >
          <b>נבחרו {selected.length} סרטונים לבדיקה</b>
          <button className='btn btn-sm btn-light' onClick={() => setSelected([])}>
            ניקוי
          </button>
          <button className='btn btn-sm btn-primary' onClick={goTests}>
            המשך לבניית הבדיקה ←
          </button>
        </div>
      )}
    </div>
  )
}

export default AdsGrid
